import clsx from 'clsx';
import React, { useState } from 'react';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  Fact,
  IconMail,
  IconSearch,
  Input,
  ListPage,
  Pagination,
  Spinner,
  StatusPill,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TranscriptView,
} from '../../ui/v2';
import { parseAdminCallTranscript } from './adminCallsModel';
import {
  SENT_NOTIFICATIONS_PAGE_SIZE,
  sentNotificationContact,
  sentNotificationEvent,
  sentNotificationPreview,
  sentNotificationStatus,
  sentNotificationStatusTone,
  sentNotificationWhen,
  type SentNotification,
} from './adminSentNotificationsModel';
import type { SentNotificationSortKey } from './platformAdminOrdering';
import type { AdminSentNotificationsState } from './useAdminSentNotifications';

const COLUMNS: {
  key: SentNotificationSortKey | 'transcript';
  label: string;
  sortable?: boolean;
}[] = [
  { key: 'sentAt', label: 'Sent', sortable: true },
  { key: 'store', label: 'Store', sortable: true },
  { key: 'contact', label: 'Contact', sortable: true },
  { key: 'channel', label: 'Channel', sortable: true },
  { key: 'event', label: 'Event', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'message', label: 'Message', sortable: true },
  { key: 'transcript', label: 'Transcript' },
];

const Placeholder: React.FC<{ value: React.ReactNode }> = ({ value }) =>
  value === '—' ? <span className="text-v2-fg-3">—</span> : <>{value}</>;

const AdminSentNotificationsTab: React.FC<AdminSentNotificationsState> = ({
  rows,
  loading,
  page,
  totalPages,
  count,
  search,
  appliedSearch,
  listError,
  sort,
  setPage,
  toggleSort,
  onSearchChange,
}) => {
  const [selected, setSelected] = useState<SentNotification | null>(null);
  const showInitialLoading = loading && rows.length === 0;
  const isRefreshing = loading && rows.length > 0;
  const filtering = appliedSearch.length > 0;

  return (
    <ListPage
      title="Sent notifications"
      toolbar={
        <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
          <div className="w-full min-w-0 sm:w-[320px]">
            <Input
              type="search"
              size="sm"
              aria-label="Search sent notifications"
              placeholder="Search store, contact, event, or message…"
              value={search}
              onChange={onSearchChange}
              leading={<IconSearch size={14} />}
            />
          </div>
        </div>
      }
      actions={
        isRefreshing && (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
          >
            <Spinner />
            Updating notifications...
          </span>
        )
      }
    >
      {listError && (
        <div className="px-4 pt-4 lg:px-6">
          <Callout tone="danger">{listError}</Callout>
        </div>
      )}

      {!listError && !showInitialLoading && rows.length === 0 ? (
        <EmptyState
          icon={<IconMail />}
          title={
            filtering
              ? 'No notifications match that search'
              : 'No notifications have been sent'
          }
          description={
            filtering
              ? 'Try a store name, contact, event, or a few words from the message.'
              : 'When a store notification goes out, it will show up here.'
          }
        />
      ) : rows.length === 0 && !showInitialLoading ? null : (
        <>
          {showInitialLoading && (
            <p role="status" className="sr-only">
              Loading sent notifications…
            </p>
          )}
          <div
            className={clsx(
              'v2-scroll overflow-x-auto transition-opacity',
              isRefreshing && 'opacity-70',
            )}
          >
            <Table className="min-w-[1200px]">
              <THead>
                <tr>
                  {COLUMNS.map((column) => (
                    <TH
                      key={column.key}
                      sortable={column.sortable && !showInitialLoading}
                      active={column.sortable && sort.key === column.key}
                      dir={sort.direction}
                      onSort={() =>
                        column.sortable &&
                        toggleSort(column.key as SentNotificationSortKey)
                      }
                    >
                      {column.label}
                    </TH>
                  ))}
                </tr>
              </THead>
              <TBody>
                {showInitialLoading ? (
                  <TableSkeletonRows cols={COLUMNS.length} />
                ) : (
                  rows.map((row) => {
                    const status = sentNotificationStatus(row);
                    return (
                      <TR
                        key={row.id}
                        onActivate={() => setSelected(row)}
                        selected={selected?.id === row.id}
                      >
                        <TD num className="whitespace-nowrap">
                          {sentNotificationWhen(row)}
                        </TD>
                        <TD className="font-medium">
                          <Placeholder value={row.store_name || '—'} />
                        </TD>
                        <TD className="whitespace-nowrap">
                          {sentNotificationContact(row)}
                        </TD>
                        <TD>
                          <Tag>{row.channel || '—'}</Tag>
                        </TD>
                        <TD>
                          <div className="font-medium">
                            {sentNotificationEvent(row)}
                          </div>
                          {row.category && row.event_label && (
                            <div className="text-v2-12 text-v2-fg-3">
                              {row.event_label}
                            </div>
                          )}
                        </TD>
                        <TD>
                          {status === '—' ? (
                            <Placeholder value="—" />
                          ) : (
                            <StatusPill
                              tone={sentNotificationStatusTone(status)}
                            >
                              {status}
                            </StatusPill>
                          )}
                        </TD>
                        <TD className="max-w-[280px] truncate text-v2-fg-2">
                          {sentNotificationPreview(row.body)}
                        </TD>
                        <TD>
                          {row.transcript ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`View notification #${row.id}`}
                              onClick={() => setSelected(row)}
                              className="-ml-2.5"
                            >
                              View
                            </Button>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </div>
        </>
      )}

      {(rows.length > 0 || showInitialLoading) && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={count}
          pageSize={SENT_NOTIFICATIONS_PAGE_SIZE}
          onPage={setPage}
          noun="notifications"
        />
      )}

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? sentNotificationEvent(selected) : 'Notification'}
        description={selected?.subject || undefined}
        size="lg"
      >
        {selected && (
          <>
            <dl className="grid grid-cols-2 gap-3 border-y border-v2-line py-3">
              <Fact label="Store" value={selected.store_name || '—'} />
              <Fact label="Contact" value={sentNotificationContact(selected)} />
              <Fact label="Channel" value={selected.channel || '—'} />
              <Fact label="Status" value={sentNotificationStatus(selected)} />
              <Fact label="Sent" value={sentNotificationWhen(selected)} />
              <Fact
                label="Call ID"
                value={selected.call_id || '—'}
                missing={!selected.call_id}
              />
            </dl>

            <h3 className="mt-5 text-v2-13 font-semibold text-v2-fg">
              Message sent
            </h3>
            <p className="mt-2 whitespace-pre-wrap text-v2-14 leading-[22px] text-v2-fg">
              {selected.body || 'No message body was stored.'}
            </p>

            <h3 className="mt-5 text-v2-13 font-semibold text-v2-fg">
              Call transcript
            </h3>
            <div
              role="region"
              aria-label="Call transcript"
              tabIndex={0}
              className="v2-scroll mt-2 max-h-[40vh] overflow-y-auto pr-2"
            >
              {selected.transcript ? (
                <TranscriptView
                  lines={parseAdminCallTranscript(selected.transcript)}
                />
              ) : (
                <p className="text-v2-12 text-v2-fg-3">
                  No call transcript for this notification.
                </p>
              )}
            </div>
          </>
        )}
      </Dialog>
    </ListPage>
  );
};

export default AdminSentNotificationsTab;
